import {
  Badge,
  Box,
  Button,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarPane,
  SidebarPaneBody,
  SidebarPaneFooter,
  SidebarPaneHeader,
  SidebarPaneHeaderReveal,
  SidebarPaneTitle,
  SidebarSeparator,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type IconName,
} from "@salestrak/ui";
import { FeatureFlagKey, useFeatureFlags } from "#app/hooks/use-feature-flags";
import React from "react";
import { isGuestTier } from "@salestrak/permissions";

import { useAppData } from "#app/lib/data";
import { LogoutConfirmDialog } from "#app/components/auth/logout-confirm-dialog";
import { useAutomationsDialogActions } from "#app/lib/automations-dialog-context";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { useIntegrationsDialogActions } from "#app/lib/integrations-dialog-context";
import { useDiscovery } from "#app/lib/use-discovery";
import { useManageDialogDoors } from "#app/components/navigation/use-manage-dialog-doors";
import { usePermission } from "#app/lib/hooks/use-permission";
import { canEdit } from "#app/lib/utils/access-level";
import {
  useSlashSearchTarget,
  VIEW_SEARCH_SHORTCUT,
} from "#app/lib/hooks/use-slash-search";
import { SearchFieldHotkeyTooltip } from "#app/components/keyboard-shortcuts/search-field-hotkey-tooltip";
import { AnalyticsContext, SettingsEvent, trackEvent, useTrackSearch } from "#app/lib/analytics";

const trackSettingsResult = (kind: "page" | "row" | "workflow", page?: string) =>
  trackEvent({
    context: AnalyticsContext.Settings,
    event: SettingsEvent.SelectSearchResult,
    data: { kind, ...(page ? { page } : {}) },
  });
import { SidebarSection } from "@salestrak/ui";
import { CollapseSidebarButton } from "#app/components/navigation/collapse-sidebar-button";
import { useSettingsDoors } from "#app/components/navigation/use-settings-doors";
import { useSidebarResize } from "#app/components/navigation/use-sidebar-resize";
import {
  requestSettingsHighlight,
  searchSettingsNav,
  type SettingsSearchPage,
} from "#app/components/settings-search/settings-search";
import {
  ADMINISTRATION_ITEMS,
  AUTOMATIONS_ITEM,
  CUSTOM_FIELDS_ITEM,
  FEATURE_ITEMS,
  INTEGRATIONS_ITEM,
  USER_SETTINGS,
  isNavItemVisible,
  type GatedNavItemData,
} from "./settings-nav-items";
import { useSidebarNav } from "./use-sidebar-nav";
import { WorkflowItem } from "./workflow-item";
import type { Omittable } from "@salestrak/ui";

/** One settings destination. `discovery` trails the row with a "New" badge —
 *  a dot on a nav icon reads as an unread count, a labeled chip says what it
 *  means — and the row's own handler dismisses it. */
function SettingsNavRow({
  icon,
  selectedIcon,
  label,
  isActive,
  discovery = false,
  onClick,
}: Omittable<{
  icon: IconName;
  selectedIcon?: IconName;
  label: string;
  isActive: boolean;
  discovery?: boolean;
  onClick: () => void;
}>) {
  return (
    <SidebarMenuItem>
      {/* Glyph and label inherit the row's color: SidebarMenuButton keeps an
        * inactive row muted (through hover too) and promotes only the active
        * row, so the selected wash and full-strength text carry the emphasis. */}
      <SidebarMenuButton isActive={isActive} onClick={onClick}>
        <Icon name={isActive && selectedIcon != null ? selectedIcon : icon} />
        <Text truncate>{label}</Text>
      </SidebarMenuButton>
      {discovery && (
        <SidebarMenuBadge>
          {/* brand, not primary: a tinted chip is accent ink on a neutral
            * surface, never the solid workspace-accent fill. */}
          <Badge color="brand" size="tag" tone="soft">
            New
          </Badge>
        </SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  );
}

export function SettingsSidebar() {
  const { leaveSettings } = useSettingsDoors();
  const { navigate, isActive } = useSidebarNav();
  const { workflows, currentUser } = useAppData();
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  const { openDialog: openIntegrationsDialog } = useIntegrationsDialogActions();
  const { openDialog: openAutomationsDialog } = useAutomationsDialogActions();
  const sidebarResize = useSidebarResize();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);
  // Bare `/` focuses this search on a settings screen whose page has no view
  // search of its own; where the page has one, that search sits later in the
  // document and takes the key, and this tooltip drops the keycap.
  const isSlashTarget = useSlashSearchTarget(searchRef);

  const formsEnabled = useFeatureFlags().hasFlag(FeatureFlagKey.FORMS);
  // Pre-resolve every gated key so we can branch deterministically.
  // Hooks-in-a-loop would be unsafe; explicit lookups are clearer anyway.
  const perms: Record<string, boolean> = {
    "create.roles": usePermission("create.roles"),
    "delete.items": usePermission("delete.items"),
    "forms.manage": usePermission("forms.manage") && formsEnabled,
    "issue_sources.manage": usePermission("issue_sources.manage"),
    "issue_types.manage": usePermission("issue_types.manage"),
    "manage.edit_workflows": usePermission("manage.edit_workflows"),
    "manage.teams": usePermission("manage.teams"),
    "manage.users": usePermission("manage.users"),
    "reasons.manage": usePermission("reasons.manage"),
    "trash.manage": usePermission("trash.manage"),
    "workspace.integrations": usePermission("workspace.integrations"),
  };
  const canCreateWorkflows = usePermission("create.workflows");
  const canEditWorkflows = perms["manage.edit_workflows"];

  // Discovery flags, keyed like `perms` above so render loops can branch
  // without hooks-in-a-loop. Add an entry per `discoveryKey` in use.
  const discoveries: Record<string, ReturnType<typeof useDiscovery>> = {
    forms: useDiscovery("forms"),
    permissions: useDiscovery("permissions"),
  };
  const itemDiscovery = (item: GatedNavItemData) =>
    item.discoveryKey ? discoveries[item.discoveryKey] : undefined;

  // Vocabulary pages (`readOnlyView`) are reference material for every
  // internal viewer; a Guest (every held role at the Guest rung) stays
  // outside workspace configuration entirely.
  const readOnlyViews = !isGuestTier(currentUser?.roles);
  const canSeeItem = (item: GatedNavItemData) =>
    isNavItemVisible(item, (key) => perms[key] === true, { readOnlyViews });
  const visibleUserSettings = USER_SETTINGS.filter(canSeeItem);
  const visibleFeatureItems = FEATURE_ITEMS.filter(canSeeItem);
  const visibleAdminItems = ADMINISTRATION_ITEMS.filter(canSeeItem);
  const {
    showCustomFields: showCustomFieldsItem,
    showAutomations: showAutomationsItem,
    showIntegrations: showIntegrationsItem,
  } = useManageDialogDoors();
  // Per-workflow rows in "Workflows" jump straight into that
  // workflow's settings — only useful when the viewer actually has
  // edit-level access (or higher).
  const editableWorkflows = workflows.filter((wf) =>
    canEdit(wf.viewerAccessLevel),
  );

  // Everything the sidebar can reach, in nav order, already gated — the
  // search's input, so results can never show a page the sidebar wouldn't.
  const routedPage = (item: GatedNavItemData): SettingsSearchPage => ({
    key: item.route,
    icon: item.icon,
    selectedIcon: item.selectedIcon,
    label: item.label,
    onOpen: () => navigate(item.route),
  });
  const searchablePages: SettingsSearchPage[] = [
    ...visibleUserSettings.map(routedPage),
    ...visibleFeatureItems.map(routedPage),
    ...visibleAdminItems.map(routedPage),
    ...(showCustomFieldsItem
      ? [{
          key: "dialog:custom-fields",
          icon: CUSTOM_FIELDS_ITEM.icon,
          label: CUSTOM_FIELDS_ITEM.label,
          // Called with the click event by the row; the opener reads its
          // argument as an edit-field intent, so it is wrapped here.
          onOpen: () => openCustomFieldsDialog(),
        }]
      : []),
    ...(showAutomationsItem
      ? [{
          key: "dialog:automations",
          icon: AUTOMATIONS_ITEM.icon,
          label: AUTOMATIONS_ITEM.label,
          onOpen: () => openAutomationsDialog(),
        }]
      : []),
    ...(showIntegrationsItem
      ? [{
          key: "dialog:integrations",
          icon: INTEGRATIONS_ITEM.icon,
          label: INTEGRATIONS_ITEM.label,
          onOpen: openIntegrationsDialog,
        }]
      : []),
  ];
  const searching = query.trim().length > 0;
  const searchResults = searching
    ? searchSettingsNav(query, searchablePages, perms)
    : [];
  // The dynamic per-workflow rows join the search too — they are visible menu
  // options like any page, just entity data rather than catalog. Same gate as
  // the Workflows section they normally live in.
  const q = query.trim().toLowerCase();
  const matchingWorkflows =
    searching && canEditWorkflows
      ? editableWorkflows.filter((wf) =>
          (wf.name ?? "").toLowerCase().includes(q),
        )
      : [];
  useTrackSearch(query, () => ({
    context: AnalyticsContext.Settings,
    event: SettingsEvent.Search,
    data: { resultCount: searchResults.length + matchingWorkflows.length },
  }));

  return (
    // The same pane the module menu uses — this replaces it on settings routes,
    // resize seam and all, so the island stays stable across the swap.
    <SidebarPane
      {...sidebarResize}
    >
      {/* The module-menu header's grammar: the title leads at the reading edge
        * and every control trails it. Leaving settings is this header's one
        * primary action, so it wears the shell the module menus give "+" —
        * an outline control on the `control` shadow. */}
      <SidebarPaneHeader
        actions={
          <>
            {/* Yields its width at rest (see the module menus) so the title
              * row carries only the always-visible back control. */}
            <SidebarPaneHeaderReveal>
              <CollapseSidebarButton />
            </SidebarPaneHeaderReveal>
            {/* The shadow rides a pill wrapper matching the Button's stadium:
              * the outline variant manages its own shadow per state, so a
              * shadow on the button itself would be clobbered on press. Flex,
              * not block — inline layout adds descender space below the button
              * and the shadow pokes out under it. */}
            <Box direction="row" rounded="full" shadow="control">
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      aria-label="Back to app"
                      onClick={leaveSettings}
                      size="icon-sm"
                      variant="outline"
                      // Light: no border — the `control` shadow (which carries
                      // a 1px hairline ring, see --control-shadow) gives the
                      // edge instead. Dark keeps its already-visible border.
                      className="not-dark:border-transparent!"
                    >
                      {/* The header's primary action — opt the glyph out of
                        * Button's `opacity-80` dim so it reads crisp. */}
                      <Icon name="arrow-left" size="sm" className="opacity-100" />
                    </Button>
                  }
                />
                <TooltipPopup side="top">Back to app</TooltipPopup>
              </Tooltip>
            </Box>
          </>
        }
      >
        <SidebarPaneTitle>Settings</SidebarPaneTitle>
      </SidebarPaneHeader>

      {/* The sidemenu search (the keyboard-shortcuts pattern), pinned above
          the scroll on the body's own gutter, on the sidebar-header control
          rung (28px: `size="sm"`). The header's own 8px gutter sits above it;
          the 4px top pad opens that to 12px so the field clears the back
          button instead of hanging off it, and the 8px bottom pad keeps it
          off the first row. */}
      <Box paddingBottom="sm" paddingTop="xs" paddingX="base" shrink={false}>
        <SearchFieldHotkeyTooltip
          keys={isSlashTarget ? VIEW_SEARCH_SHORTCUT : undefined}
          label="Search settings"
        >
          <InputGroup>
            <InputGroupAddon>
              <Icon name="search" />
            </InputGroupAddon>
            <InputGroupInput
              ref={searchRef}
              aria-label="Search settings"
              placeholder="Search..."
              size="sm"
              type="search"
              value={query}
              onValueChange={setQuery}
              onKeyDown={(e) => {
                // Esc clears before anything else hears it — the ladder every
                // search field in the app follows.
                if (e.key === "Escape" && query.length > 0) {
                  e.stopPropagation();
                  setQuery("");
                }
              }}
            />
          </InputGroup>
        </SearchFieldHotkeyTooltip>
      </Box>

      <SidebarPaneBody label="Settings">
        {searching ? (
          searchResults.length === 0 && matchingWorkflows.length === 0 ? (
            <SidebarGroup>
              <Box paddingX="sm" paddingY="xs">
                <Text size="sm" variant="faint">
                  No settings match
                </Text>
              </Box>
            </SidebarGroup>
          ) : (
            <SidebarGroup>
              <SidebarMenu>
                {searchResults.map(({ page, rows }) => (
                  <React.Fragment key={page.key}>
                    <SettingsNavRow
                      icon={page.icon}
                      selectedIcon={page.selectedIcon}
                      isActive={isActive(page.key)}
                      label={page.label}
                      onClick={() => {
                        trackSettingsResult("page", page.key);
                        page.onOpen();
                      }}
                    />
                    {/* ps-9 lands result labels on the page rows' label edge
                        (8px pad + 16px glyph + 12px gap), matching the
                        shortcuts dialog's result rows. */}
                    {rows.map((row) => (
                      <SidebarMenuItem key={row.anchor}>
                        <SidebarMenuButton
                          className="ps-9"
                          onClick={() => {
                            trackSettingsResult("row", page.key);
                            requestSettingsHighlight(row.anchor);
                            page.onOpen();
                          }}
                        >
                          <Text size="sm" truncate>
                            {row.label}
                          </Text>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </React.Fragment>
                ))}
                {/* Matching workflows render as their real entity rows —
                    never a hand-rolled icon+label pair. */}
                {matchingWorkflows.map((wf) => (
                  <WorkflowItem
                    isActive={isActive(`/settings/workflows/${wf.workflowId}`)}
                    key={wf.workflowId}
                    onClick={() => {
                      trackSettingsResult("workflow");
                      navigate(`/settings/workflows/${wf.workflowId}`);
                    }}
                    workflow={wf}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroup>
          )
        ) : (
        <>
          <SidebarGroup>
            <SidebarMenu>
              {visibleUserSettings.map((item) => (
                <SettingsNavRow
                  icon={item.icon}
                  selectedIcon={item.selectedIcon}
                  isActive={isActive(item.route, item.exact)}
                  key={item.route}
                  label={item.label}
                  onClick={() => navigate(item.route)}
                />
              ))}
            </SidebarMenu>
          </SidebarGroup>

          {visibleFeatureItems.length > 0 && (
            <SidebarSection spacing="compact" title="Features">
              {visibleFeatureItems.map((item) => {
                const discovery = itemDiscovery(item);
                return (
                  <SettingsNavRow
                    discovery={discovery?.visible}
                    icon={item.icon}
                    selectedIcon={item.selectedIcon}
                    isActive={isActive(item.route)}
                    key={item.route}
                    label={item.label}
                    onClick={() => {
                      discovery?.dismiss();
                      navigate(item.route);
                    }}
                  />
                );
              })}
            </SidebarSection>
          )}

          {(visibleAdminItems.length > 0 ||
            showCustomFieldsItem ||
            showAutomationsItem ||
            showIntegrationsItem) && (
            <SidebarSection spacing="compact" title="Administration">
              {visibleAdminItems.map((item) => {
                const discovery = itemDiscovery(item);
                return (
                  <SettingsNavRow
                    discovery={discovery?.visible}
                    icon={item.icon}
                    selectedIcon={item.selectedIcon}
                    isActive={isActive(item.route, item.exact)}
                    key={item.route}
                    label={item.label}
                    onClick={() => {
                      discovery?.dismiss();
                      navigate(item.route);
                    }}
                  />
                );
              })}
              {showCustomFieldsItem && (
                <SettingsNavRow
                  icon={CUSTOM_FIELDS_ITEM.icon}
                  isActive={false}
                  label={CUSTOM_FIELDS_ITEM.label}
                  onClick={() => openCustomFieldsDialog()}
                />
              )}
              {showAutomationsItem && (
                <SettingsNavRow
                  icon={AUTOMATIONS_ITEM.icon}
                  isActive={false}
                  label={AUTOMATIONS_ITEM.label}
                  onClick={() => openAutomationsDialog()}
                />
              )}
              {showIntegrationsItem && (
                <SettingsNavRow
                  icon={INTEGRATIONS_ITEM.icon}
                  isActive={false}
                  label={INTEGRATIONS_ITEM.label}
                  onClick={openIntegrationsDialog}
                />
              )}
            </SidebarSection>
          )}

          {canEditWorkflows && (
            <SidebarSection spacing="compact" title="Workflows">
              {editableWorkflows.map((wf) => (
                <WorkflowItem
                  isActive={isActive(`/settings/workflows/${wf.workflowId}`)}
                  key={wf.workflowId}
                  onClick={() =>
                    navigate(`/settings/workflows/${wf.workflowId}`)
                  }
                  workflow={wf}
                />
              ))}
              {canCreateWorkflows && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => navigate("/settings/workflows?create=true")}
                  >
                    <Icon color="faint" name="plus" />
                    <Text truncate variant="faint">
                      Create a Workflow
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarSection>
          )}
        </>
        )}
      </SidebarPaneBody>

      {/* Pinned footer (reference settings grammar): a divider, then the one
        * session action. The row only opens the shared confirm dialog — the
        * dialog owns the sign-out call and its analytics event. */}
      <SidebarSeparator />
      <SidebarPaneFooter>
        <SidebarGroup>
          <SidebarMenu>
            <SettingsNavRow
              icon="log-out"
              isActive={false}
              label="Log out"
              onClick={() => setLogoutConfirmOpen(true)}
            />
          </SidebarMenu>
        </SidebarGroup>
      </SidebarPaneFooter>
      <LogoutConfirmDialog
        via="settings_sidebar"
        onOpenChange={setLogoutConfirmOpen}
        open={logoutConfirmOpen}
      />
    </SidebarPane>
  );
}
